import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BrStatesUtil } from 'app/core/utils/br-state.utils';
import { SelectOptionWithColor } from 'app/shared/components/button/components/button-multi-select/button-multi-select.component';
import { TitleWithCaptionComponent } from 'app/shared/components/table/components/table/cell-components/title-with-caption.component';
import { ColumnDatatable } from 'app/shared/components/table/components/table/models/columnDatatable.model';
import {
  TableChangeEvent,
  TableState,
} from 'app/shared/components/table/components/table/models/tableEvent.model';
import { TableComponent } from 'app/shared/components/table/components/table/table.component';
import { PaginatorState } from 'primeng/paginator';
import {
  BehaviorSubject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { ProgressBarCellComponent } from '../../../../shared/components/table/components/table/cell-components/progress-bar-cell.component';
import { StatusIndicatorCellComponent } from '../../../../shared/components/table/components/table/cell-components/status-indicator-cell.component';
import { StatusFilterHeaderComponent } from '../../../../shared/components/table/components/table/header-components/status-filter-header.component';
import { AppointmentHistoricResponseDTO } from '../../dtos/appointment-historic-response.dto';
import { CouponExpiryStatus } from '../../enums/coupon-expiry-status.enum';
import { CouponApiService } from '../../services/coupon-api.service';
import { NumberUtils } from 'app/core/utils/number.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppointmentStatusDisplay } from '../../enums/appointment-status-display.enum';

interface SearchForm {
  search: FormControl<string | null>;
}

@Component({
  selector: 'app-purchase-history',
  templateUrl: './purchase-history.component.html',
  styleUrl: './purchase-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseHistoryComponent implements OnInit, AfterViewInit {
  tableTotalRecords = signal(100);
  tableData$!: Observable<AppointmentHistoricResponseDTO[]>;
  selectedPurchase$ =
    new BehaviorSubject<AppointmentHistoricResponseDTO | null>(null);
  couponApiService = inject(CouponApiService);
  brStatesUtil = BrStatesUtil.getInstance();
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  searchForm: FormGroup<SearchForm>;

  rows = 10;

  statusOptions: SelectOptionWithColor[] = [
    { label: 'Todos', value: 'all' },
    {
      label: 'Aguardando pagamento',
      value: AppointmentStatusDisplay.AWAITING_PAYMENT,
      color: 'var(--p-secondary-500)',
    },
    {
      label: 'Consulta perdida',
      value: AppointmentStatusDisplay.APPOINTMENT_LOST,
      color: 'var(--p-info-500)',
    },
    {
      label: 'Consulta concluída',
      value: AppointmentStatusDisplay.APPOINTMENT_DONE,
      color: 'var(--p-background-800)',
    },
    {
      label: 'Pagamento efetuado',
      value: AppointmentStatusDisplay.PAYMENT_DONE,
      color: 'var(--p-success-500)',
    },
    {
      label: 'Cupom vencido',
      value: AppointmentStatusDisplay.EXPIRED_COUPON,
      color: 'var(--p-error-500)',
    },
    {
      label: 'Cupom estornado',
      value: AppointmentStatusDisplay.REVERSED_COUPON,
      color: 'var(--p-background-200)',
    },
    {
      label: 'Consulta cancelada',
      value: AppointmentStatusDisplay.APPOINTMENT_CANCELED,
      color: 'var(--p-background-200)',
    },
    {
      label: 'Aguardando compra',
      value: AppointmentStatusDisplay.AWAITING_PURCHASE,
      color: 'var(--p-secondary-500)',
    },
  ];

  columns: ColumnDatatable<AppointmentHistoricResponseDTO>[] = [
    {
      headerName: 'Cliente',
      field: 'name',
      cellComponent: TitleWithCaptionComponent,
      sortable: true,
      colSpan: 5,
      custonConfig: {
        captionPrefix: 'CPF',
        captionField: 'cpf',
      },
    },
    {
      headerName: 'Status',
      field: 'appointmentDisplayStatus',
      sortable: true,
      colSpan: 3,
      cellComponent: StatusIndicatorCellComponent,
      headerComponent: StatusFilterHeaderComponent,
      custonConfig: {
        options: this.statusOptions,
        optionLabel: 'label',
        optionValue: 'value',
        keyFilter: 'appointmentDisplayStatus',
        getDotColor: (rowData: AppointmentHistoricResponseDTO) => {
          return this.statusOptions.find(
            status => status.value === rowData.appointmentDisplayStatus,
          )?.color;
        },
        getLabel: (rowData: AppointmentHistoricResponseDTO) => {
          return this.statusOptions.find(
            status => status.value === rowData.appointmentDisplayStatus,
          )?.label;
        },
      },
    },
    {
      headerName: 'Vencimento do cupom',
      field: 'couponDaysToExpire',
      sortable: true,
      colSpan: 2,
      cellComponent: ProgressBarCellComponent,
      custonConfig: {
        getReverseMode: (rowData: AppointmentHistoricResponseDTO) => {
          return (
            rowData.appointmentDisplayStatus ===
            AppointmentStatusDisplay.EXPIRED_COUPON
          );
        },
        getProgressValue: (rowData: AppointmentHistoricResponseDTO) =>
          rowData.couponDaysToExpire ?? 0,
        getProgressColor: (rowData: AppointmentHistoricResponseDTO) =>
          this.getProgressBarColor(this.getExpiryStatus(rowData)),
        getProgressText: (rowData: AppointmentHistoricResponseDTO) => {
          return this.formatExpiryDays(
            rowData.couponDaysToExpire ?? 0,
            rowData.appointmentDisplayStatus,
          );
        },
        show: (rowData: AppointmentHistoricResponseDTO) => {
          return ![
            AppointmentStatusDisplay.AWAITING_PURCHASE,
            AppointmentStatusDisplay.AWAITING_PAYMENT,
            AppointmentStatusDisplay.REVERSED_COUPON,
          ].includes(rowData.appointmentDisplayStatus);
        },
      },
    },
    {
      headerName: 'Data da consulta',
      field: 'datetime',
      sortable: true,
      colSpan: 2,
      cellComponent: ProgressBarCellComponent,
      custonConfig: {
        getReverseMode: () => false,
        getProgressValue: (rowData: AppointmentHistoricResponseDTO) =>
          rowData.appointmentDaysToExpire ?? 0,
        getProgressColor: (rowData: AppointmentHistoricResponseDTO) =>
          this.getAppointmentProgressBarColor(rowData),
        getProgressText: (rowData: AppointmentHistoricResponseDTO) => {
          return this.formatAppointmentDate(
            rowData.datetime,
            rowData.appointmentDisplayStatus,
            rowData.appointmentDaysToExpire,
          );
        },
      },
    },
  ];

  @ViewChild('datatable')
  table!: TableComponent<AppointmentHistoricResponseDTO>;

  private params$ = new BehaviorSubject<Partial<TableState>>({});

  constructor() {
    this.searchForm = this.fb.group<SearchForm>({
      search: this.fb.control(''),
    });
  }

  ngOnInit(): void {
    this.tableData$ = this.params$.pipe(
      switchMap(params => {
        const page = params.paginatorState?.page ?? 0;
        const pageSize = params.paginatorState?.rows ?? this.rows;

        const headerFilter = params.headerFilter || {};

        const appointmentDisplayStatus = (headerFilter[
          'appointmentDisplayStatus'
        ] ?? []) as AppointmentStatusDisplay & 'all'[];

        const statusCleaned = (
          appointmentDisplayStatus.includes('all')
            ? []
            : appointmentDisplayStatus
        ) as AppointmentStatusDisplay[];

        const searchTerm = (headerFilter['search'] as string) || '';

        let customerName: string | undefined = undefined;
        let cpf: string | undefined = undefined;

        if (searchTerm) {
          const cleanedTerm = searchTerm.replace(/\D/g, '');
          if (
            cleanedTerm.length > 0 &&
            cleanedTerm.length === searchTerm.replace(/[.-\s]/g, '').length
          ) {
            cpf = searchTerm;
          } else {
            customerName = searchTerm;
          }
        }

        const apiParams = {
          page: page + 1,
          pageSize,
          sortField: params.sortState?.field,
          sortOrder: params.sortState?.order,
          customerName,
          cpf,
          status: statusCleaned,
        };

        return this.couponApiService.listAppointments(apiParams).pipe(
          map(
            response =>
              response.response || {
                items: [],
                totalItems: 0,
                totalPages: 0,
                currentPage: 0,
                offset: 0,
              },
          ),
          tap(result => {
            this.tableTotalRecords.set(result.totalItems);
            this.cdr.markForCheck();
          }),
          map(pagination => pagination.items),
        );
      }),
    );

    this.searchForm.controls.search.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(value => {
        const headerFilter = this.params$.value.headerFilter || {};
        headerFilter['search'] = value || '';

        this.params$.next({
          ...this.params$.value,
          headerFilter,
        });
      });
  }

  ngAfterViewInit(): void {
    if (this.table) {
      const initialPurchase = this.selectedPurchase$.getValue();
      if (initialPurchase) {
        this.table.selectedItems.set([initialPurchase]);
      }

      this.cdr.markForCheck();
    }
  }

  onSelectPurchase(purchase: AppointmentHistoricResponseDTO) {
    this.selectedPurchase$.next(purchase);
    this.cdr.markForCheck();
  }

  onTableChange(event: TableChangeEvent) {
    this.params$.next({ ...this.params$.value, ...event });
  }

  onPageChange(event: PaginatorState) {
    this.params$.next({
      ...this.params$.value,
      paginatorState: event,
    });
    this.cdr.markForCheck();
  }

  private readonly expiryDaysFormatMap = new Map<
    AppointmentStatusDisplay,
    (days: number) => string
  >([
    [AppointmentStatusDisplay.APPOINTMENT_DONE, () => 'Cupom utilizado'],
    [AppointmentStatusDisplay.AWAITING_PURCHASE, () => ''],
    [AppointmentStatusDisplay.REVERSED_COUPON, () => ''],
    [
      AppointmentStatusDisplay.EXPIRED_COUPON,
      (days: number) => `Vencido em ${Math.abs(days)} dias`,
    ],
  ]);

  formatExpiryDays(
    days: number | undefined,
    status: AppointmentStatusDisplay,
  ): string {
    if (!days && days !== 0) return '';

    if (days < 0) {
      return `Vencido em ${Math.abs(days)} dias`;
    }

    const formatter = this.expiryDaysFormatMap.get(status);
    if (formatter) {
      return formatter(days);
    }

    return `${days} dias para o vencimento`;
  }

  formatAppointmentDate(
    datetime: Date | undefined,
    status: AppointmentStatusDisplay,
    daysToAppointment?: number,
  ): string {
    switch (true) {
      case [AppointmentStatusDisplay.APPOINTMENT_DONE].includes(status):
        return 'Consulta realizada';

      case daysToAppointment && daysToAppointment > 0:
        return `${daysToAppointment} dias para a consulta`;

      case daysToAppointment === 0:
        return 'Hoje';

      case daysToAppointment && daysToAppointment < 0:
        return `Vencido em ${Math.abs(daysToAppointment)} dias`;

      case !!datetime:
        return new Date(datetime).toLocaleDateString();

      default:
        return '';
    }
  }

  getExpiryStatus(
    appointment: AppointmentHistoricResponseDTO,
  ): CouponExpiryStatus {
    const { appointmentDisplayStatus, couponDaysToExpire } = appointment;
    switch (appointmentDisplayStatus) {
      case AppointmentStatusDisplay.EXPIRED_COUPON:
        return CouponExpiryStatus.EXPIRED;

      case AppointmentStatusDisplay.APPOINTMENT_DONE:
        return CouponExpiryStatus.USED;

      default:
        if (couponDaysToExpire && couponDaysToExpire > 20) {
          return CouponExpiryStatus.OK;
        }

        if (
          couponDaysToExpire &&
          NumberUtils.isNumberInRange(couponDaysToExpire, 10, 20)
        ) {
          return CouponExpiryStatus.WARNING;
        }

        return CouponExpiryStatus.CLOSE_TO_EXPIRATION;
    }
  }

  getProgressBarColor(expiryStatus: CouponExpiryStatus): string {
    switch (expiryStatus) {
      case CouponExpiryStatus.EXPIRED:
        return 'var(--p-error-400)';
      case CouponExpiryStatus.WARNING:
        return 'var(--p-warning-400)';
      case CouponExpiryStatus.OK:
      case CouponExpiryStatus.USED:
        return 'var(--p-success-400)';
      default:
        return '';
    }
  }

  getAppointmentProgressBarColor(
    appointment: AppointmentHistoricResponseDTO,
  ): string {
    const { appointmentDisplayStatus, appointmentDaysToExpire } = appointment;
    switch (appointmentDisplayStatus) {
      case AppointmentStatusDisplay.APPOINTMENT_DONE:
        return 'var(--p-success-400)';

      case AppointmentStatusDisplay.APPOINTMENT_LOST:
        return 'var(--p-error-400)';

      default:
        if (appointmentDaysToExpire && appointmentDaysToExpire > 20) {
          return 'var(--p-success-400)';
        }

        if (
          appointmentDaysToExpire &&
          NumberUtils.isNumberInRange(appointmentDaysToExpire, 10, 20)
        ) {
          return 'var(--p-warning-400)';
        }

        if (
          appointmentDaysToExpire &&
          NumberUtils.isNumberInRange(appointmentDaysToExpire, 0, 10)
        ) {
          return 'var(--p-error-400)';
        }
        return 'var(--p-gray-300)';
    }
  }

  navigateToDetails(purchase: AppointmentHistoricResponseDTO) {
    if (purchase && purchase.cpf) {
      this.router.navigate(['/coupon/purchase-details', purchase.id]);
    }
  }
}
